import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { renderQrCodeDataUrl } from '@/utils/barcodeRenderer';
import { AssetService } from '@/services/AssetService';

export type LabelType = 'kiosk' | 'shelf' | 'box';
export type ConditionCode = 'A' | 'B' | 'H';

interface BarcodeLabelPrintDialogProps {
  open: boolean;
  onClose: () => void;
  mapping?: {
    barcode_value: string;
    location_name: string;
    location_path: string;
    catalog_name?: string;
    lin?: string | null;
    old_lin?: string | null;
    nsn?: string | null;
    color?: string | null;
    unit_of_issue?: string | null;
    part_number?: string | null;
    warehouse_location_name?: string | null;
    storefront_location_name?: string | null;
  };
  catalogId?: string | null;
  locationId?: string | null;
  initialData?: {
    catalog_name?: string;
    location_name?: string;
    location_path?: string;
    lin?: string | null;
    old_lin?: string | null;
    nsn?: string | null;
    color?: string | null;
    unit_of_issue?: string | null;
    part_number?: string | null;
    warehouse_location_name?: string | null;
    storefront_location_name?: string | null;
  };
}

export const BarcodeLabelPrintDialog: React.FC<BarcodeLabelPrintDialogProps> = ({
  open,
  onClose,
  mapping: providedMapping,
  catalogId,
  locationId,
  initialData,
}) => {
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [fetchedMapping, setFetchedMapping] = useState<{
    barcode_value: string;
    location_name: string;
    location_path: string;
    catalog_name?: string;
    lin?: string | null;
    old_lin?: string | null;
    nsn?: string | null;
    color?: string | null;
    unit_of_issue?: string | null;
    part_number?: string | null;
    warehouse_location_name?: string | null;
    storefront_location_name?: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [labelType, setLabelType] = useState<LabelType>('shelf');
  const [conditionCode, setConditionCode] = useState<ConditionCode>('A');
  const [isPrinting, setIsPrinting] = useState(false);

  const mapping = providedMapping || fetchedMapping;

  useEffect(() => {
    if (!open) {
      setQrImageUrl(null);
      setFetchedMapping(null);
      setError(null);
      setLabelType('shelf');
      setConditionCode('A');
      return;
    }

    if (providedMapping) {
      return;
    }

    if (!catalogId || !locationId) {
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await AssetService.getOrCreateBarcodeMapping(catalogId, locationId);
        if (cancelled) return;
        if (!result) throw new Error('No barcode mapping returned');

        setFetchedMapping({
          barcode_value: result.barcode_value,
          location_name: initialData?.location_name || '',
          location_path: initialData?.location_path || '',
          catalog_name: initialData?.catalog_name || '',
          lin: initialData?.lin,
          old_lin: initialData?.old_lin,
          nsn: initialData?.nsn,
          color: initialData?.color,
          unit_of_issue: initialData?.unit_of_issue,
          part_number: initialData?.part_number,
          warehouse_location_name: initialData?.warehouse_location_name,
          storefront_location_name: initialData?.storefront_location_name,
        });
      } catch (err) {
        console.error('Failed to fetch barcode mapping', err);
        if (!cancelled) setError('Failed to load barcode.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, providedMapping, catalogId, locationId, initialData]);

  useEffect(() => {
    if (open && mapping?.barcode_value) {
      setQrLoading(true);
      setQrImageUrl(null);
      renderQrCodeDataUrl(mapping.barcode_value)
        .then((url) => {
          setQrImageUrl(url);
        })
        .catch((err) => {
          console.error('Failed to render barcode', err);
        })
        .finally(() => {
          setQrLoading(false);
        });
    }
  }, [open, mapping?.barcode_value]);

  const handlePrint = () => {
    if (!qrImageUrl || qrLoading || isPrinting) return;
    setIsPrinting(true);
    window.addEventListener('afterprint', () => setIsPrinting(false), { once: true });
    window.print();
  };

  const getLogoSrc = () => {
    return labelType === 'kiosk' ? '/q-logo.png' : '/equip-logo.png';
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle>Print Barcode Label</DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'hidden', pt: 2.5 }}>
          <Stack spacing={3} sx={{ mt: 1.5, minHeight: '100%' }}>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Label Type</InputLabel>
                <Select
                  value={labelType}
                  label="Label Type"
                  onChange={(e) => setLabelType(e.target.value as LabelType)}
                >
                  <MenuItem value="kiosk">Kiosk Label (1x1&quot;)</MenuItem>
                  <MenuItem value="shelf">Shelf Label (1x2&quot;)</MenuItem>
                  <MenuItem value="box">Warehouse Box Label (Full Page)</MenuItem>
                </Select>
              </FormControl>

              {labelType === 'box' && (
                <FormControl fullWidth size="small">
                  <InputLabel>Condition Code</InputLabel>
                  <Select
                    value={conditionCode}
                    label="Condition Code"
                    onChange={(e) => setConditionCode(e.target.value as ConditionCode)}
                  >
                    <MenuItem value="A">Condition A</MenuItem>
                    <MenuItem value="B">Condition B</MenuItem>
                    <MenuItem value="H">Condition H</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>

            {loading && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ py: 4 }}
              >
                <CircularProgress />
                <Typography>Loading barcode...</Typography>
              </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && mapping && (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  p: 2,
                  backgroundColor: 'grey.50',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Preview
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    p: 1,
                  }}
                >
                  {qrLoading && <CircularProgress size={40} />}

                  {!qrLoading && qrImageUrl && (
                    <div className={`screen-label screen-type-${labelType}`}>
                      {labelType === 'kiosk' && (
                        <>
                          <div className="qr-container">
                            <img src={qrImageUrl} className="qr-img" alt="QR" />
                            <img src={getLogoSrc()} className="qr-logo" alt="Logo" />
                          </div>
                          <div className="item-name">{mapping.catalog_name}</div>
                          {mapping.part_number && <div className="part-num">PN: {mapping.part_number}</div>}
                        </>
                      )}

                      {labelType === 'shelf' && (
                        <>
                          <div className="qr-container">
                            <img src={qrImageUrl} className="qr-img" alt="QR" />
                            <img src={getLogoSrc()} className="qr-logo" alt="Logo" />
                          </div>
                          <div className="text-container">
                            <div className="shelf-meta">{[mapping.lin && `LIN: ${mapping.lin}`, mapping.nsn && `NSN: ${mapping.nsn}`].filter(Boolean).join(' | ')}</div>
                            <div className="item-name">{mapping.catalog_name}</div>
                            <div className="shelf-location">{mapping.location_name}</div>
                            <div className="barcode-val">{mapping.barcode_value}</div>
                          </div>
                        </>
                      )}

                      {labelType === 'box' && (
                        <>
                          <div className="box-header">
                            <div>LIN: {mapping.lin || 'N/A'}{mapping.old_lin ? ` (Old LIN: ${mapping.old_lin})` : ''}</div>
                            <div>NSN: {mapping.nsn || 'N/A'}</div>
                          </div>
                          <div className="box-title">{mapping.catalog_name}</div>
                          <div className="box-details">
                            <strong>UI:</strong> {mapping.unit_of_issue || 'EA'} &nbsp;|&nbsp;
                            <strong>Color:</strong> {mapping.color || 'N/A'} &nbsp;|&nbsp;
                            <strong>Condition:</strong> {conditionCode}
                          </div>
                          <div className="box-location">Location: {mapping.location_name}</div>
                          {(mapping.warehouse_location_name || mapping.storefront_location_name) && (
                            <div className="box-sub-locations">
                              {mapping.warehouse_location_name && <>Warehouse: {mapping.warehouse_location_name}</>}
                              {mapping.warehouse_location_name && mapping.storefront_location_name && ' | '}
                              {mapping.storefront_location_name && <>Storefront: {mapping.storefront_location_name}</>}
                            </div>
                          )}

                          <div className="qr-container">
                            <img src={qrImageUrl} className="qr-img" alt="QR" />
                            <img src={getLogoSrc()} className="qr-logo" alt="Logo" />
                          </div>

                          <table className="tracking-grid">
                            <thead>
                              <tr>
                                <th style={{ width: '30%' }}>BOX QTY</th>
                                <th style={{ width: '40%' }}>DATE</th>
                                <th style={{ width: '30%' }}>INITIALS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[1, 2, 3, 4, 5].map((i) => (
                                <tr key={i}>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </div>
                  )}
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            color="primary"
            disabled={!mapping || loading || qrLoading || !qrImageUrl || isPrinting}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          .screen-label {
            font-family: Arial, sans-serif;
            color: #000;
            background: #fff;
            border: 1px solid #999;
            box-sizing: border-box;
          }

          .screen-label .qr-img { width: 100%; height: 100%; display: block; object-fit: contain; }
          .screen-label .qr-logo { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; border-radius: 4px; }

          .screen-type-kiosk {
            height: min(100%, 340px);
            aspect-ratio: 1 / 1;
            width: auto;
            max-width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 18px;
            gap: 8px;
          }
          .screen-type-kiosk .qr-container { position: relative; width: 58%; aspect-ratio: 1 / 1; flex-shrink: 0; }
          .screen-type-kiosk .qr-logo { width: 22%; padding: 2%; }
          .screen-type-kiosk .item-name { font-size: clamp(20px, 3vh, 40px); font-weight: 700; text-align: center; line-height: 1.2; }
          .screen-type-kiosk .part-num { font-size: clamp(14px, 1.8vh, 24px); text-align: center; line-height: 1.1; color: #000; }

          .screen-type-shelf {
            height: min(100%, 320px);
            aspect-ratio: 2 / 1;
            width: auto;
            max-width: 100%;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            padding: 18px;
            gap: 20px;
          }
          .screen-type-shelf .qr-container { position: relative; height: 100%; aspect-ratio: 1 / 1; flex-shrink: 0; }
          .screen-type-shelf .qr-logo { width: 35%; padding: 2%; }
          .screen-type-shelf .text-container { display: flex; flex-direction: column; justify-content: center; flex: 1; overflow: hidden; }
          .screen-type-shelf .shelf-meta { font-size: clamp(13px, 1.5vh, 20px); line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .screen-type-shelf .item-name { font-size: clamp(24px, 3.2vh, 42px); font-weight: 700; line-height: 1.2; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
          .screen-type-shelf .shelf-location { font-size: clamp(13px, 1.5vh, 20px); line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .screen-type-shelf .barcode-val { font-size: clamp(14px, 1.7vh, 22px); font-family: monospace; color: #000; margin-top: 4px; }

          .screen-type-box {
            height: min(100%, 420px);
            aspect-ratio: 8.5 / 11;
            width: auto;
            max-width: 100%;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            justify-content: flex-start;
            padding: 16px 24px;
            box-sizing: border-box;
          }
          .screen-type-box .box-header { display: flex; justify-content: space-between; font-size: clamp(10px, 1.2vh, 14px); font-weight: 700; border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 10px; }
          .screen-type-box .box-title { font-size: clamp(18px, 2.3vh, 30px); font-weight: 700; text-align: center; margin: 10px 0; line-height: 1.2; }
          .screen-type-box .box-details { font-size: clamp(11px, 1.3vh, 16px); text-align: center; margin-bottom: 10px; }
          .screen-type-box .box-location { font-size: clamp(12px, 1.5vh, 20px); text-align: center; color: #000; margin-bottom: 4px; }
          .screen-type-box .box-sub-locations { font-size: clamp(10px, 1.2vh, 14px); text-align: center; color: #000; margin-bottom: 10px; }
          .screen-type-box .qr-container { position: relative; width: min(46%, 160px); aspect-ratio: 1 / 1; margin: 0 auto 10px auto; }
          .screen-type-box .qr-logo { width: 20%; padding: 2%; }
          .screen-type-box .tracking-grid { width: 100%; border-collapse: collapse; margin-top: auto; }
          .screen-type-box .tracking-grid th, .screen-type-box .tracking-grid td { border: 2px solid #000; padding: 4px; text-align: center; font-size: clamp(10px, 1.1vh, 13px); }
          .screen-type-box .tracking-grid th { background-color: #f0f0f0; }
          .screen-type-box .tracking-grid td { height: 16px; }
        `}
      </style>

      {/* Hidden printable area with high specificity print styles */}
      {mapping && (
        <>
          <style>
            {`
          @media print {
            @page {
              margin: 0;
              size: ${labelType === 'kiosk' ? '1in 1in' : labelType === 'shelf' ? '2in 1in' : 'letter'};
            }
            body * { visibility: hidden !important; }
            #printable-barcode-label, #printable-barcode-label * { visibility: visible !important; }
            #printable-barcode-label {
              display: flex !important;
              position: fixed !important;
              left: 50% !important;
              top: 50% !important;
              transform: translate(-50%, -50%) !important;
              background-color: white !important;
              z-index: 999999 !important;
              font-family: Arial, sans-serif !important;
            }
            
            /* Kiosk Label (1x1) */
            .print-type-kiosk {
              width: 100vw !important;
              height: 100vh !important;
              flex-direction: column !important;
              align-items: center !important;
              justify-content: center !important;
              padding: 0.05in !important;
              box-sizing: border-box !important;
            }
            .print-type-kiosk .qr-container { position: relative; width: 0.58in; height: 0.58in; flex-shrink: 0; }
            .print-type-kiosk .item-name { 
              font-size: 5.5pt; 
              font-weight: bold; 
              text-align: center; 
              line-height: 1.2; 
              overflow: hidden; 
              width: 0.9in; 
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              margin-top: 0.02in; 
            }
            .print-type-kiosk .part-num {
              font-size: 5pt;
              text-align: center;
              color: #000;
              line-height: 1.1;
              width: 0.9in;
            }
            
            /* Shelf Label (2x1) */
            .print-type-shelf {
              width: 100vw !important;
              height: 100vh !important;
              flex-direction: row !important;
              align-items: center !important;
              justify-content: center !important;
              padding: 0.06in 0.08in !important;
              box-sizing: border-box !important;
            }
            .print-type-shelf .qr-container { position: relative; width: 0.82in; height: 0.82in; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
            .print-type-shelf .text-container { margin-left: 0.07in; display: flex; flex-direction: column; justify-content: center; flex: 1; overflow: hidden; }
            .print-type-shelf .shelf-meta { font-size: 6pt; color: #000; line-height: 1.2; margin-bottom: 0.01in; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .print-type-shelf .item-name { font-size: 7.5pt; font-weight: bold; line-height: 1.25; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
            .print-type-shelf .shelf-location { font-size: 6pt; color: #000; line-height: 1.2; margin-top: 0.01in; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .print-type-shelf .barcode-val { font-size: 5.5pt; font-family: monospace; color: #000; margin-top: 0.01in; }
            
            /* Box Label (Full Page) */
            .print-type-box {
              width: 100vw !important;
              height: 100vh !important;
              flex-direction: column !important;
              align-items: stretch !important;
              justify-content: flex-start !important;
              padding: 0.5in 1in !important;
              box-sizing: border-box !important;
            }
            .print-type-box .box-header { display: flex; justify-content: space-between; font-size: 24pt; font-weight: bold; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
            .print-type-box .box-title { font-size: 36pt; font-weight: bold; text-align: center; margin: 30px 0; line-height: 1.2; }
            .print-type-box .box-details { font-size: 24pt; text-align: center; margin-bottom: 30px; }
            .print-type-box .box-location { font-size: 20pt; text-align: center; color: #000; margin-bottom: 10px; }
            .print-type-box .box-sub-locations { font-size: 14pt; text-align: center; color: #000; margin-bottom: 40px; }
            .print-type-box .qr-container { position: relative; width: 350px; height: 350px; margin: 0 auto 50px auto; }
            .print-type-box .tracking-grid { width: 100%; border-collapse: collapse; margin-top: auto; }
            .print-type-box .tracking-grid th, .print-type-box .tracking-grid td { border: 2px solid #000; padding: 15px; text-align: center; font-size: 18pt; }
            .print-type-box .tracking-grid th { background-color: #f0f0f0; }
            .print-type-box .tracking-grid td { height: 100px; }

            /* Shared QR Logo Overlay */
            .qr-img { width: 100%; height: 100%; display: block; object-fit: contain; }
            .qr-logo { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 2%; border-radius: 4px; }
            .print-type-kiosk .qr-logo { width: 22%; }
            .print-type-shelf .qr-logo { width: 35%; }
            .print-type-box .qr-logo { width: 20%; padding: 2%; }
          }
        `}
          </style>

          <div
            id="printable-barcode-label"
            className={`print-type-${labelType}`}
            style={{ display: 'none' }}
          >
            {labelType === 'kiosk' && (
              <>
                <div className="qr-container">
                  {qrImageUrl && <img src={qrImageUrl} className="qr-img" alt="QR" />}
                  {qrImageUrl && <img src={getLogoSrc()} className="qr-logo" alt="Logo" />}
                </div>
                <div className="item-name">{mapping.catalog_name}</div>
                {mapping.part_number && <div className="part-num">PN: {mapping.part_number}</div>}
              </>
            )}

            {labelType === 'shelf' && (
              <>
                <div className="qr-container">
                  {qrImageUrl && <img src={qrImageUrl} className="qr-img" alt="QR" />}
                  {qrImageUrl && <img src={getLogoSrc()} className="qr-logo" alt="Logo" />}
                </div>
                <div className="text-container">
                  <div className="shelf-meta">{[mapping.lin && `LIN: ${mapping.lin}`, mapping.nsn && `NSN: ${mapping.nsn}`].filter(Boolean).join(' | ')}</div>
                  <div className="item-name">{mapping.catalog_name}</div>
                  <div className="shelf-location">{mapping.location_name}</div>
                  <div className="barcode-val">{mapping.barcode_value}</div>
                </div>
              </>
            )}

            {labelType === 'box' && (
              <>
                <div className="box-header">
                  <div>LIN: {mapping.lin || 'N/A'}{mapping.old_lin ? ` (Old LIN: ${mapping.old_lin})` : ''}</div>
                  <div>NSN: {mapping.nsn || 'N/A'}</div>
                </div>
                <div className="box-title">{mapping.catalog_name}</div>
                <div className="box-details">
                  <strong>UI:</strong> {mapping.unit_of_issue || 'EA'} &nbsp;|&nbsp;
                  <strong>Color:</strong> {mapping.color || 'N/A'} &nbsp;|&nbsp;
                  <strong>Condition:</strong> {conditionCode}
                </div>
                <div className="box-location">
                  Location: {mapping.location_name}
                </div>
                {(mapping.warehouse_location_name || mapping.storefront_location_name) && (
                  <div className="box-sub-locations">
                    {mapping.warehouse_location_name && <>Warehouse: {mapping.warehouse_location_name}</>}
                    {mapping.warehouse_location_name && mapping.storefront_location_name && ' | '}
                    {mapping.storefront_location_name && <>Storefront: {mapping.storefront_location_name}</>}
                  </div>
                )}

                <div className="qr-container">
                  {qrImageUrl && <img src={qrImageUrl} className="qr-img" alt="QR" />}
                  {qrImageUrl && <img src={getLogoSrc()} className="qr-logo" alt="Logo" />}
                </div>

                <table className="tracking-grid">
                  <thead>
                    <tr>
                      <th style={{ width: '30%' }}>BOX QTY</th>
                      <th style={{ width: '40%' }}>DATE</th>
                      <th style={{ width: '30%' }}>INITIALS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Render 5 empty rows for manual entry */}
                    {[1, 2, 3, 4, 5].map((i) => (
                      <tr key={i}>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
